import type { UserRole } from '@/types/domain'

// The user manual lives in docs/handbuch (Markdown for Git, the same file renders in the app and as PDF).
// Each chapter (## heading) carries a marker `<!-- rollen: member trainer admin -->` for the roles that see it in the app.

export type ManualChapter = {
  id: string
  title: string
  markdown: string
  sections: { id: string; title: string }[]
}

export type Manual = {
  title: string
  intro: string
  chapters: ManualChapter[]
}

const ALL_ROLES: UserRole[] = ['member', 'trainer', 'admin']

/** Heading anchor like GitHub creates it, so links in the Markdown work in Git and in the app alike. */
export function slugify(heading: string): string {
  return heading
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]/gu, '')
    .replace(/\s/g, '-')
}

export function parseManual(source: string, role: UserRole): Manual {
  // The written table of contents is for Git and PDF; the app renders its own navigation.
  const text = source.replace(/<!-- toc -->[\s\S]*?<!-- \/toc -->\n?/, '')
  const [head, ...parts] = text.split(/^(?=## )/m)
  const title = head.match(/^# (.+)$/m)?.[1]?.trim() ?? 'Handbuch'
  const intro = head.replace(/^# .+$/m, '').trim()

  const chapters = parts
    .map((part) => {
      const roles = part.match(/<!-- rollen: ([^>]*?) -->/)?.[1].split(/\s+/) ?? ALL_ROLES
      const chapterTitle = part.match(/^## (.+)$/m)![1].trim()
      const markdown = part.replace(/<!-- rollen: [^>]*? -->\n?/, '')
      const sections = [...markdown.matchAll(/^### (.+)$/gm)].map((m) => ({ id: slugify(m[1]), title: m[1].trim() }))
      return { roles, chapter: { id: slugify(chapterTitle), title: chapterTitle, markdown, sections } }
    })
    .filter(({ roles }) => roles.includes(role))
    .map(({ chapter }) => chapter)

  return { title, intro, chapters }
}
